import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import { Action } from '../../actions';
import {
    acceptCampaignAndGroupSelectorData,
    acceptCampaignAndGroupSelectorDataDna,
    fetchCampaignAndGroupSelectorCampaignsData,
    fetchCampaignAndGroupSelectorGroupsData,
    selectCampaignAndGroupSelectorCampaign,
    selectCampaignAndGroupSelectorGroup
} from '../../actions/campaignAndGroupSelector';
import {
    CampaignAndGroupSelector,
    CampaignAndGroupSelectorProps
} from '../../components/CampaignAndGroupSelector/CampaignAndGroupSelector';
import { SelectorItems } from '../../components/CampaignAndGroupSelector/Selector/Selector';
import {
    BANNERS_IN_CAMPAIGN_LIMIT,
    BANNERS_IN_GROUP_LIMIT,
    INTERNAL_BANNERS_IN_CAMPAIGN_LIMIT,
    INTERNAL_BANNERS_IN_GROUP_LIMIT
} from '../../constants';
import { getAccessRightsForCampaignAndGroupSelector } from '../../service/CampaignAndGroupSelectorHelpers';
import { CampaignAndGroupSelectorEntityToSelectType } from '../../typings/sidebar';
import { State } from '../../typings/state';
import { Omit, TDispatchProps, TStateProps } from '../../typings/utils';

interface OwnProps {
    onCancel: () => void;
}

type StateProps = TStateProps<CampaignAndGroupSelectorProps>;

type DispatchProps = Omit<TDispatchProps<CampaignAndGroupSelectorProps, OwnProps>, 'onCampaignInputChange'> & {
    onCampaignInputChange: (filter: string, entityType?: CampaignAndGroupSelectorEntityToSelectType) => void;
};

type MergedProps = CampaignAndGroupSelectorProps;

const mapStateToProps = (state: State): StateProps => {
    const {
        grid: {
            campaignAndGroupSelector: {
                campaignItems,
                campaignSelected,
                selectorStatus,
                groupSelected,
                groupItems,
                entityToSelectType: type
            }
        },
        commonData: { operatorFeatures, accessRights, features }
    } = state;

    const groupBannersLimit = accessRights.canViewInternalAds
        ? INTERNAL_BANNERS_IN_GROUP_LIMIT
        : BANNERS_IN_GROUP_LIMIT;

    const campaignBannersLimit = accessRights.canViewInternalAds
        ? INTERNAL_BANNERS_IN_CAMPAIGN_LIMIT
        : BANNERS_IN_CAMPAIGN_LIMIT;

    const { canAddInDna, canAddInDnaOnly, fullyAvailable } = getAccessRightsForCampaignAndGroupSelector({
        operatorFeatures,
        clientFeatures: features,
        accessRights,
        type,
        campaign: campaignSelected,
        group: groupSelected
    });

    return {
        campaignItems,
        campaignSelected,
        selectorStatus,
        groupItems,
        groupSelected,
        type,
        canAddInDna,
        canAddInDnaOnly,
        groupBannersLimit,
        campaignBannersLimit,
        fullyAvailable
    };
};

const mapDispatchToProps = (dispatch: Dispatch<Action>, ownProps: OwnProps): DispatchProps => {
    return {
        onAccept: (): void => {
            dispatch(acceptCampaignAndGroupSelectorData());
            ownProps.onCancel();
        },
        onAcceptDna: (): void => {
            dispatch(acceptCampaignAndGroupSelectorDataDna());
            ownProps.onCancel();
        },
        onCampaignSelect: (selectorItems: SelectorItems): void => {
            dispatch(selectCampaignAndGroupSelectorCampaign(selectorItems));
        },
        onCampaignInputChange: (filter: string, entityType?: CampaignAndGroupSelectorEntityToSelectType): void => {
            dispatch(fetchCampaignAndGroupSelectorCampaignsData(filter, entityType));
        },
        onGroupInputChange: (filter: string): void => {
            dispatch(fetchCampaignAndGroupSelectorGroupsData(filter));
        },
        onGroupSelect: (selectorItems: SelectorItems): void => {
            dispatch(selectCampaignAndGroupSelectorGroup(selectorItems));
        }
    };
};

const mergeProps = (stateProps: StateProps, dispatchProps: DispatchProps, ownProps: OwnProps): MergedProps => {
    return {
        ...stateProps,
        ...dispatchProps,
        ...ownProps,
        onCampaignInputChange(filter: string) {
            const { type } = stateProps;
            dispatchProps.onCampaignInputChange(filter, type);
        }
    };
};

export const CampaignAndGroupSelectorContainer = connect<StateProps, DispatchProps, OwnProps, MergedProps>(
    mapStateToProps,
    mapDispatchToProps,
    mergeProps
)(CampaignAndGroupSelector);
